/**
 * foreignDocRotation — mint a fresh AsthrDocumentId for a foreign/existing doc.
 *
 * When a Word file drafted under another account is reopened, its persisted
 * ``AsthrDocumentId`` custom property points at that account's runs. Sending it
 * in ``POST /runs`` trips the backend tenancy guard
 * (``server/routes/pipeline.py`` ~1847) → ``404 No document '…'``. Rotating the
 * id before ``startRun`` gives the backend a document with no prior runs, so the
 * guard body is skipped and the (custom / fresh) run initializes cleanly.
 *
 * This is the pure, testable core of the shell's ``rotateForeignDocIfNeeded``:
 * the "is this doc foreign?" gate stays in the component (a ref latched by the
 * custom-import detector); this helper owns the rotate + cache-move ordering,
 * which is load-bearing and easy to get wrong.
 */

export interface ForeignDocRotationDeps {
  /** Mints a fresh id, replaces the AsthrDocumentId (and strips the stale
   *  AsthrActiveRunId) in the Word file. Returns "" when Office.js is
   *  unavailable. ``wordWriter.rotateDocumentId``. */
  rotate: () => Promise<string>;
  /** Drain any debounced session/run/spine saves onto the OLD id BEFORE the
   *  cache moves, so a queued patch can't get re-targeted mid-write. */
  flush: () => void;
  /** Move the module-level ``_cachedDocId`` — read by every
   *  ``scheduleSave(getCachedDocId(), …)`` and the ``startRun`` payload. */
  setCachedDocId: (id: string) => void;
  /** Move the React ``documentSessionId`` — routes chat sync writes. Must move
   *  together with ``_cachedDocId`` or session/run/spine writes and chat writes
   *  land on different ids. */
  setDocumentSessionId: (id: string) => void;
  /** Best-effort: surface a rotation failure without throwing. */
  onError?: (err: unknown) => void;
}

/**
 * Rotate the document id and move both id stores to the new value.
 *
 * Returns the new id, or ``null`` when nothing changed — Office.js unavailable
 * or rotation threw (fail-soft: the caller proceeds with the existing id, which
 * at worst reproduces the prior 404 rather than blocking the flow).
 *
 * Ordering is deliberate: flush → setCachedDocId → setDocumentSessionId, exactly
 * mirroring the shell's ``handleConfirmRotateProject``.
 */
export async function performForeignDocRotation(deps: ForeignDocRotationDeps): Promise<string | null> {
  let newId = "";
  try {
    newId = await deps.rotate();
  } catch (err) {
    deps.onError?.(err);
  }
  if (!newId) return null;
  deps.flush();
  deps.setCachedDocId(newId);
  deps.setDocumentSessionId(newId);
  return newId;
}
